import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ImageIcon } from "lucide-react";
import { Section, Overline, H1, Lead } from "@/components/ui-kit";
import { api } from "@/lib/api";

export default function Deals() {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/case-studies")
      .then((r) => {
        const data = Array.isArray(r.data) ? r.data : [];
        // Render ALL returned case studies: no filtering by published, featured, or image presence
        console.log("Case Studies:", data);
        setCaseStudies(data);
      })
      .catch((err) => {
        console.error("Case Studies fetch failed:", err);
        setCaseStudies([]);
      })
      .finally(() => setLoaded(true));
  }, []);

  return (
    <>
      <Section className="pt-12 md:pt-20 pb-8 md:pb-10">
        <Overline>Deals & Case Studies</Overline>
        <H1 className="mt-5 max-w-4xl">Strategy in plain language. Numbers when they're real.</H1>
        <Lead className="mt-7 max-w-3xl">
          A documented look at the structures, strategies, and operational decisions behind our work in Sacramento real
          estate. We update these case studies as projects close and stabilize.
        </Lead>
      </Section>
      <Section className="pb-24 md:pb-32" dataTestId="deals-grid">
        {loaded && caseStudies.length === 0 ? (
          <div
            data-testid="deals-empty"
            className="card-frame p-10 text-center text-neutral-400"
          >
            <p className="text-neutral-200">Case studies are being prepared.</p>
            <p className="text-sm mt-2">We publish project write-ups only after the facts are confirmed. Check back soon, or <Link to="/contact" className="text-[#C19B6C] hover:text-white">ask us about current projects</Link>.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
            {caseStudies.map((c, idx) => {
              const slug = c.slug || c.id || `case-${idx}`;
              const title = c.title || "Untitled case study";
              const subtitle = c.summary || c.subtitle || "";
              const location = c.location || "Roseville/Sacramento, California";
              const cover = c.cover_image;
              return (
                <Link
                  key={c.id || slug}
                  to={`/deals/${slug}`}
                  className="group block card-frame overflow-hidden p-0"
                  data-testid={`case-${slug}`}
                >
                  <div className="aspect-[16/10] overflow-hidden bg-neutral-900 flex items-center justify-center">
                    {cover ? (
                      <img
                        src={cover}
                        alt={title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <div
                        className="w-full h-full flex flex-col items-center justify-center text-neutral-600 gap-3"
                        data-testid={`case-${slug}-placeholder`}
                      >
                        <ImageIcon size={36} />
                        <span className="overline">Image coming soon</span>
                      </div>
                    )}
                  </div>
                  <div className="p-7">
                    <div className="overline">{location}{c.project_status && c.project_status !== "completed" ? ` · ${c.project_status === "in_progress" ? "In progress" : "Planned"}` : ""}</div>
                    <h3 className="font-display text-2xl mt-3">{title}</h3>
                    {subtitle ? (
                      <p className="text-sm text-neutral-400 mt-3 leading-relaxed">{subtitle}</p>
                    ) : null}
                    <div className="mt-6 inline-flex items-center gap-2 text-[#C19B6C] text-sm">
                      Read case study <ArrowUpRight size={16} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </Section>
    </>
  );
}
